import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/cn';
import { OnlineIndicator } from '@/components/ui/OnlineIndicator';
import { NotificationBell } from '@/components/layout/NotificationBell';
import { UserMenu } from '@/components/layout/UserMenu';

/* ==========================================================================
   Header — top navigation bar for the app shell.
   ========================================================================== */

export interface HeaderProps {
  /** Page title — renders in heading font. */
  title?: string;
  /** Show a back arrow that calls navigate(-1). */
  showBack?: boolean;
  /** Custom back handler (overrides default navigate(-1)). */
  onBack?: () => void;
  /** Action buttons / icons rendered on the right side. */
  actions?: ReactNode;
  /** Hide the online indicator (e.g. on pages with their own). */
  hideOnlineIndicator?: boolean;
  /** Hide the notification bell (e.g. on back-navigation pages). */
  hideNotificationBell?: boolean;
  /** Additional class names. */
  className?: string;
}

export function Header({
  title,
  showBack = false,
  onBack,
  actions,
  hideOnlineIndicator = false,
  hideNotificationBell = false,
  className,
}: HeaderProps) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-[var(--z-sticky)]',
        'flex items-center gap-3',
        'h-16 px-5',
        'bg-surface-base/80 backdrop-blur-lg',
        'border-b border-border-default',
        className,
      )}
    >
      {/* Back button */}
      {showBack && (
        <button
          onClick={handleBack}
          className={cn(
            'flex items-center justify-center',
            'h-[var(--touch-min)] w-[var(--touch-min)]',
            '-ml-2 rounded-[var(--radius-md)]',
            'text-text-primary',
            'transition-colors duration-[var(--duration-fast)]',
            'hover:bg-surface-sunken active:bg-border-default',
            'cursor-pointer',
          )}
          aria-label="Indietro"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </button>
      )}

      {/* Title */}
      {title && (
        <h1
          className={cn(
            'flex-1 truncate',
            'font-[family-name:var(--font-heading)] text-lg font-bold',
            'text-text-primary',
          )}
        >
          {title}
        </h1>
      )}

      {/* Spacer when no title */}
      {!title && <div className="flex-1" />}

      {/* Online indicator */}
      {!hideOnlineIndicator && <OnlineIndicator />}

      {/* Notification bell */}
      {!hideNotificationBell && <NotificationBell />}

      {/* Actions */}
      {actions && (
        <div className="flex items-center gap-1 shrink-0">{actions}</div>
      )}

      {/* User menu — accesso a tutte le pagine */}
      <UserMenu />
    </header>
  );
}
